import React from 'react';
import { motion } from 'framer-motion';
import { IoCalendar, IoTime, IoArrowForward } from 'react-icons/io5';

const BlogCard = ({ post, index }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <motion.article
      variants={cardVariants}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group bg-card-dark/50 backdrop-blur-sm border border-jet/30 rounded-xl overflow-hidden hover:border-accent-yellow/30 transition-all duration-500 glow-hover cursor-pointer"
    >
      {/* Post Thumbnail */}
      <div className="relative overflow-hidden">
        <img
          src={post.thumbnail}
          alt={post.title}
          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-accent-yellow/90 text-bg-dark text-xs font-semibold rounded-full backdrop-blur-sm">
            {post.category}
          </span>
        </div>

        {/* Read More Icon */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-10 h-10 bg-accent-yellow/90 backdrop-blur-sm rounded-full flex items-center justify-center text-bg-dark">
            <IoArrowForward size={16} />
          </div>
        </div>
      </div>

      {/* Post Content */}
      <div className="p-6">
        {/* Meta Information */}
        <div className="flex items-center gap-4 text-light-gray text-sm mb-3">
          <div className="flex items-center gap-1">
            <IoCalendar size={14} className="text-accent-yellow" />
            <span>{formatDate(post.date)}</span>
          </div>
          <div className="flex items-center gap-1">
            <IoTime size={14} className="text-accent-yellow" />
            <span>{post.readTime}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-white-1 mb-3 group-hover:text-accent-yellow transition-colors duration-300 line-clamp-2">
          {post.title}
        </h3>
        
        {/* Excerpt */}
        <p className="text-light-gray text-sm leading-relaxed mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.slice(0, 3).map((tag, tagIndex) => (
            <span
              key={tagIndex}
              className="px-2 py-1 text-xs bg-jet/50 text-accent-yellow rounded border border-accent-yellow/20"
            >
              {tag}
            </span>
          ))}
          {post.tags.length > 3 && (
            <span className="text-xs text-light-gray">
              +{post.tags.length - 3} more
            </span>
          )}
        </div>

        {/* Read More Link */}
        <div className="flex items-center justify-between">
          <span className="text-accent-yellow font-medium text-sm group-hover:text-orange-yellow-crayola transition-colors duration-300">
            Read More
          </span>
          <motion.div
            className="text-accent-yellow group-hover:translate-x-1 transition-transform duration-300"
            whileHover={{ x: 4 }}
          >
            <IoArrowForward size={16} />
          </motion.div>
        </div>
      </div>
    </motion.article>
  );
};

export default BlogCard;
